import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, UserPlus, ShieldCheck, Loader2, Edit3, Power } from "lucide-react";

const humanize = (k) =>
  k.replace(/_/g, " ")
   .replace(/\b\w/g, (c) => c.toUpperCase());

/**
 * Reusable manager-administration panel.
 *
 *   role         "Sub-Admin" | "Store Manager" | "Warehouse Manager"  (display only)
 *   apiBase      "/admin/sub-admins" | "/store/managers" | "/warehouse/managers"
 *   testIdRoot   data-testid prefix for E2E
 */
export default function ManagersPanel({ role, apiBase, testIdRoot }) {
  const [list, setList] = useState([]);
  const [permOptions, setPermOptions] = useState({ available: [], default: [] });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | {} (new) | doc (edit)

  const load = async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([
        api.get(apiBase),
        api.get(`${apiBase}/permissions/options`),
      ]);
      setList(a.data || []);
      setPermOptions(b.data || { available: [], default: [] });
    } catch (e) {
      toast.error(formatApiError(e) || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [apiBase]);

  const removeOne = async (mgr) => {
    if (!window.confirm(`Delete ${role.toLowerCase()} "${mgr.name}"?`)) return;
    try {
      await api.delete(`${apiBase}/${mgr.id}`);
      toast.success(`${role} deleted`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleActive = async (mgr) => {
    const next = mgr.status === "ACTIVE" ? "INACTIVE" : "ACTIVE";
    try {
      await api.patch(`${apiBase}/${mgr.id}`, { status: next });
      toast.success(`${mgr.name} → ${next}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <section
      data-testid={`${testIdRoot}-panel`}
      className="border border-slate-200 dark:border-slate-800 rounded-lg bg-slate-100/40 dark:bg-slate-900/40"
    >
      <header className="flex items-center justify-between gap-3 px-5 py-4 border-b border-slate-200 dark:border-slate-800">
        <div className="flex items-center gap-3">
          <ShieldCheck className="w-5 h-5 text-cyan-400" />
          <div>
            <h3 className="font-display font-semibold text-slate-900 dark:text-white">{role}s</h3>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
              Delegate access with granular per-user permissions.
            </p>
          </div>
        </div>
        <Button
          data-testid={`${testIdRoot}-new-btn`}
          onClick={() => setEditing({})}
          className="bg-cyan-400 text-slate-950 hover:bg-cyan-300 font-medium"
        >
          <UserPlus className="w-4 h-4 mr-1.5" /> Add {role}
        </Button>
      </header>

      <div className="p-5">
        {loading ? (
          <div className="text-sm text-slate-500 flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : list.length === 0 ? (
          <div className="text-sm text-slate-500 py-4 text-center">
            No {role.toLowerCase()}s yet. Click "Add {role}" to invite your first.
          </div>
        ) : (
          <ul className="space-y-2" data-testid={`${testIdRoot}-list`}>
            {list.map((m) => (
              <li
                key={m.id}
                data-testid={`${testIdRoot}-row-${m.id}`}
                className="flex items-center gap-3 px-3 py-2.5 rounded-md bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800"
              >
                <div className="w-9 h-9 rounded-full grid place-items-center bg-cyan-400/10 text-cyan-400 font-semibold text-sm">
                  {(m.name || m.email).slice(0, 1).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <div className="font-medium truncate text-slate-900 dark:text-white">{m.name}</div>
                    <span className={`text-[9px] font-mono-tech uppercase tracking-wider px-1.5 py-0.5 rounded ${
                      m.status === "ACTIVE"
                        ? "bg-green-500/10 text-green-400 border border-green-500/30"
                        : "bg-slate-500/10 text-slate-400 border border-slate-500/30"
                    }`}>
                      {m.status}
                    </span>
                  </div>
                  <div className="text-xs text-slate-500 truncate">{m.email}</div>
                  <div className="text-[10px] text-slate-400 font-mono-tech mt-0.5">
                    {(m.permissions?.length || 0)} / {permOptions.available.length} permissions
                  </div>
                </div>
                <Button
                  data-testid={`${testIdRoot}-toggle-${m.id}`}
                  size="sm" variant="ghost" title={m.status === "ACTIVE" ? "Deactivate" : "Activate"}
                  onClick={() => toggleActive(m)}
                  className="text-slate-400 hover:text-yellow-400 h-8 px-2"
                >
                  <Power className="w-4 h-4" />
                </Button>
                <Button
                  data-testid={`${testIdRoot}-edit-${m.id}`}
                  size="sm" variant="ghost"
                  onClick={() => setEditing(m)}
                  className="text-slate-400 hover:text-cyan-400 h-8 px-2"
                >
                  <Edit3 className="w-4 h-4" />
                </Button>
                <Button
                  data-testid={`${testIdRoot}-del-${m.id}`}
                  size="sm" variant="ghost"
                  onClick={() => removeOne(m)}
                  className="text-slate-400 hover:text-red-400 h-8 px-2"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {editing !== null && (
        <ManagerFormDialog
          role={role}
          apiBase={apiBase}
          testIdRoot={testIdRoot}
          permOptions={permOptions}
          existing={editing.id ? editing : null}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </section>
  );
}

function ManagerFormDialog({ role, apiBase, testIdRoot, permOptions, existing, onClose, onSaved }) {
  const isEdit = !!existing;
  const [name, setName] = useState(existing?.name || "");
  const [email, setEmail] = useState(existing?.email || "");
  const [phone, setPhone] = useState(existing?.phone || "");
  const [password, setPassword] = useState("");
  const [perms, setPerms] = useState(new Set(
    existing?.permissions || permOptions.default || []
  ));
  const [saving, setSaving] = useState(false);

  const togglePerm = (k) => {
    setPerms(prev => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k); else next.add(k);
      return next;
    });
  };

  const submit = async () => {
    if (!name.trim() || !email.trim()) {
      toast.error("Name and email are required.");
      return;
    }
    if (!isEdit && !password.trim()) {
      toast.error("Password is required.");
      return;
    }
    setSaving(true);
    try {
      if (isEdit) {
        const body = { name, phone, permissions: Array.from(perms) };
        if (password.trim()) body.password = password;
        await api.patch(`${apiBase}/${existing.id}`, body);
        toast.success(`${role} updated`);
      } else {
        await api.post(apiBase, {
          name, email, phone, password,
          permissions: Array.from(perms),
        });
        toast.success(`${role} created`);
      }
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        data-testid={`${testIdRoot}-form-dialog`}
        className="max-w-2xl bg-slate-950 border border-slate-800 text-white"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-xl">
            {isEdit ? `Edit ${role}` : `Invite new ${role}`}
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label className="text-[10px] font-mono-tech uppercase tracking-wider text-slate-500">Name</Label>
            <Input data-testid={`${testIdRoot}-form-name`} value={name} onChange={(e) => setName(e.target.value)} className="mt-1 bg-slate-900 border-slate-800" />
          </div>
          <div>
            <Label className="text-[10px] font-mono-tech uppercase tracking-wider text-slate-500">Email</Label>
            <Input
              data-testid={`${testIdRoot}-form-email`}
              type="email" value={email}
              disabled={isEdit}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1 bg-slate-900 border-slate-800 disabled:opacity-60"
            />
          </div>
          <div>
            <Label className="text-[10px] font-mono-tech uppercase tracking-wider text-slate-500">Phone (optional)</Label>
            <Input data-testid={`${testIdRoot}-form-phone`} value={phone} onChange={(e) => setPhone(e.target.value)} className="mt-1 bg-slate-900 border-slate-800" />
          </div>
          <div>
            <Label className="text-[10px] font-mono-tech uppercase tracking-wider text-slate-500">
              {isEdit ? "New password (leave blank to keep current)" : "Password"}
            </Label>
            <Input
              data-testid={`${testIdRoot}-form-password`}
              type="password" value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1 bg-slate-900 border-slate-800"
              placeholder={isEdit ? "•••••••• (unchanged)" : ""}
            />
          </div>
        </div>

        <div className="mt-2">
          <div className="flex items-center justify-between mb-2">
            <Label className="text-[10px] font-mono-tech uppercase tracking-wider text-slate-500">Permissions</Label>
            <div className="flex gap-3">
              <button
                data-testid={`${testIdRoot}-form-perms-all`}
                type="button"
                onClick={() => setPerms(new Set(permOptions.available))}
                className="text-[11px] text-cyan-400 hover:text-cyan-300 font-mono-tech"
              >
                Select all
              </button>
              <button
                data-testid={`${testIdRoot}-form-perms-none`}
                type="button"
                onClick={() => setPerms(new Set())}
                className="text-[11px] text-slate-500 hover:text-white font-mono-tech"
              >
                Clear
              </button>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-72 overflow-y-auto p-3 bg-slate-900/60 border border-slate-800 rounded-md">
            {permOptions.available.map((k) => (
              <label
                key={k}
                className="flex items-center gap-2 text-sm text-slate-200 cursor-pointer hover:text-white"
              >
                <Checkbox
                  data-testid={`${testIdRoot}-form-perm-${k}`}
                  checked={perms.has(k)}
                  onCheckedChange={() => togglePerm(k)}
                />
                <span className="font-mono-tech text-xs">{humanize(k)}</span>
              </label>
            ))}
          </div>
        </div>

        <DialogFooter className="mt-4">
          <Button
            data-testid={`${testIdRoot}-form-cancel`}
            variant="ghost" disabled={saving} onClick={onClose}
            className="text-slate-400 hover:text-white"
          >
            Cancel
          </Button>
          <Button
            data-testid={`${testIdRoot}-form-submit`}
            disabled={saving} onClick={submit}
            className="bg-cyan-400 text-slate-950 hover:bg-cyan-300 font-medium"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-1.5" />}
            {isEdit ? "Save changes" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
